import Dashboard from "./ndash";
import KycOptions from "../journey/kyc/kycoptions";
import CkycVerify from "../journey/kyc/ckyc";
import SetupAutopay from "../journey/mandate/setupaotopay";
import AgreementSigning from "../journey/agreement/agreement";
import LoanSummary from "../dashboard/loansummary";
import DownPayment from "../journey/downPayment/downpayment";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import LoadingAnimation from "../../components/LoadingAnimation"
import type { RootState, AppDispatch } from "../../store";

import { getJourney } from "../../api/journey.api";
import { setJourney } from "../../store/journeySlice";

type Screen =
    | "dashboard"
    | "kycOptions"
    | "ckycVerify"
    | "setupAutopay"
    | "agreementSigning"
    | "downPayment"
    | "loanSummary";

const LoanApplicationFlow = () => {
    /*
     * =========================================================
     * JOURNEY FROM REDUX
     * =========================================================
     *
     * Redux is the single source of truth for:
     *
     * - currentStep
     * - completedSteps
     * - stepSequence
     * - locked steps
     * - availableSubSteps
     * - selectedSubStep
     * - customer information
     * - approval limit
     *
     * We do NOT maintain any of these locally.
     */
    const journey = useSelector(
        (state: RootState) => state.journey.journey
    );
    const dispatch = useDispatch<AppDispatch>();

    const [isRestoringJourney, setIsRestoringJourney] =
        useState(false);

    useEffect(() => {
        const restoreJourney = async () => {
            // Redux already has journey
            if (journey) {
                return;
            }

            const token =
                localStorage.getItem("journey_token");

            // No JWT = user is genuinely unauthenticated
            if (!token) {
                return;
            }

            try {
                // console.log(
                //     "Redux journey missing. Restoring from backend..."
                // );

                setIsRestoringJourney(true);

                const response =
                    await getJourney();

                // console.log(
                //     "RESTORED JOURNEY:",
                //     response
                // );

                dispatch(
                    setJourney(response.data)
                );

            } catch (error) {
                console.error(
                    "Failed to restore journey:",
                    error
                );

            } finally {
                setIsRestoringJourney(false);
            }
        };

        restoreJourney();
    }, [journey, dispatch]);



    /*
     * =========================================================
     * LOCAL UI STATE
     * =========================================================
     *
     * This state only controls which screen/component is
     * currently open.
     *
     * It does NOT represent journey progress.
     */
    const [screen, setScreen] =
        useState<Screen>("dashboard");

    const [loanSummaryEntry, setLoanSummaryEntry] =
        useState<"dashboard" | "mandate">("dashboard");

    /*
     * =========================================================
     * CUSTOMER DATA
     * =========================================================
     */
    const customerMobile =
        journey?.customerMobileMasked ?? "";

    /*
     * =========================================================
     * START JOURNEY STEP
     * =========================================================
     *
     * Dashboard sends the actual backend step key:
     *
     * "KYC"
     * "MANDATE"
     * "AGREEMENT"
     *
     * We intentionally do NOT convert stepKey -> index.
     *
     * Backend owns the journey sequence.
     */
    const handleStartStep = (stepKey: string) => {
        switch (stepKey) {
            case "KYC":
                setScreen("kycOptions");
                break;

            case "MANDATE":
                setScreen("setupAutopay");
                break;

            case "AGREEMENT":
                setScreen("agreementSigning");
                break;

            case "DOWNPAYMENT":
                setScreen("downPayment");
                break;

            default:
                console.warn(
                    "Unknown journey step:",
                    stepKey
                );
        }
    };

 

    /*
     * =========================================================
     * MANDATE COMPLETE
     * =========================================================
     *
     * Journey progress comes from the backend response.
     */
    const handleAutopayComplete = () => {
        setLoanSummaryEntry("mandate");
        setScreen("loanSummary");
    };

    /*
     * =========================================================
     * AGREEMENT COMPLETE
     * =========================================================
     */
    const handleAgreementComplete = () => {
        setScreen("dashboard");
    };

    

    /*
     * =========================================================
     * KYC OPTION
     * =========================================================
     */
    const handleSelectCkyc = () => {
        setScreen("ckycVerify");
    };

    /*
     * =========================================================
     * JOURNEY GUARD
     * =========================================================
     *
     * AuthEntry/AuthBootstrap should normally populate Redux
     * before this component renders.
     */
    if (!journey) {
        return (
            <LoadingAnimation
                message={
                    isRestoringJourney
                        ? "Restoring your application..."
                        : "Loading your application..."
                }
            />
        );
    }

    /*
     * =========================================================
     * KYC OPTIONS
     * =========================================================
     */
    if (screen === "kycOptions") {
        return (
            <KycOptions
                onBack={() =>
                    setScreen("dashboard")
                }
                onSelectCkyc={
                    handleSelectCkyc
                }
            />
        );
    }

    /*
     * =========================================================
     * CKYC
     * =========================================================
     */
    if (screen === "ckycVerify") {
        return (
            <CkycVerify
                maskedMobileNumber={
                    customerMobile
                        ? `+91 ${customerMobile}`
                        : ""
                }
                onBack={() =>
                    setScreen("kycOptions")
                }
                onFetched={() =>
                    setScreen("dashboard")
                }
            />
        );
    }



    /*
     * =========================================================
     * MANDATE
     * =========================================================
     */
    if (screen === "setupAutopay") {
        return (
            <SetupAutopay
                availableSubSteps={
                    journey.steps.MANDATE?.availableSubSteps ?? []
                }
                onBack={() =>
                    setScreen("dashboard")
                }
                onDone={
                    handleAutopayComplete
                }
            />
        );
    }

    /*
     * =========================================================
     * AGREEMENT
     * =========================================================
     */
    if (screen === "agreementSigning") {
        return (
            <AgreementSigning
                onBack={() =>
                    setScreen("dashboard")
                }
                onDone={
                    handleAgreementComplete
                }
            />
        );
    }

    /*
     * =========================================================
     * DOWN PAYMENT
     * =========================================================
     */
    if (screen === "downPayment") {
        return (
            <DownPayment
            />
        );
    }

    /*
     * =========================================================
     * LOAN SUMMARY
     * =========================================================
     */
    if (screen === "loanSummary") {
        return (
            <LoanSummary
                entryPoint={
                    loanSummaryEntry
                }
                onBack={() =>
                    setScreen("dashboard")
                }
                onContinue={() =>
                    setScreen("dashboard")
                }
            />
        );
    }

    /*
     * =========================================================
     * DASHBOARD
     * =========================================================
     *
     * Dashboard reads the journey directly from Redux.
     *
     * No:
     * - completedCount prop
     * - customerName prop
     * - approvalLimit prop
     * - currentStep prop
     * - lockedSteps prop
     *
     * are required here.
     */
    return (
        <Dashboard
            onStartStep={
                handleStartStep
            }
            onViewLoanSummary={() => {
                setLoanSummaryEntry(
                    "dashboard"
                );

                setScreen(
                    "loanSummary"
                );
            }}
        />
    );
};

export default LoanApplicationFlow;